import { JobMatch } from '../types';

// Candidate Routes
export const CANDIDATE_ROUTES = {
    WELCOME: '/candidate/welcome',
    RESUME_UPLOAD: '/candidate/resume-upload',
    PROFILE_REVIEW: '/candidate/profile-review',
    CAREER_GOALS: '/candidate/career-goals',
    // Dashboard routes
    DASHBOARD: '/candidate/dashboard',
    MY_PROFILE: '/candidate/my-profile',
    JOBS: '/candidate/jobs',
    APPLICATIONS: '/candidate/applications',
    MESSAGES: '/candidate/messages',
    SETTINGS: '/candidate/settings',
    PAST_CHATS: '/candidate/past-chats',
} as const;

// Career Levels
export const CAREER_LEVELS = [
    { value: 'entry', label: 'Entry Level (0-2 years)' },
    { value: 'mid', label: 'Mid Level (3-5 years)' },
    { value: 'senior', label: 'Senior Level (6-10 years)' },
    { value: 'executive', label: 'Executive (10+ years)' },
] as const;

// Work Types
export const WORK_TYPES = [
    { value: 'remote', label: 'Remote', icon: '🏠' },
    { value: 'hybrid', label: 'Hybrid', icon: '🔄' },
    { value: 'onsite', label: 'On-site', icon: '🏢' },
] as const;

// Employment Types
export const EMPLOYMENT_TYPES = [
    { value: 'full-time', label: 'Full-Time', description: 'Usually 35-40 hours per week across five days.' },
    { value: 'part-time', label: 'Part-Time', description: 'Fewer than 35 hours per week, often with flexible days.' },
    { value: 'contract', label: 'Contract', description: 'Project-based work with specified deadlines and personal availability.' },
    { value: 'freelance', label: 'Freelance', description: 'Workdays vary based on project deadlines and personal availability.' },
] as const;

// Industries
export const INDUSTRIES = [
    'Technology',
    'Healthcare',
    'Finance',
    'Education',
    'Retail',
    'Manufacturing',
    'Consulting',
    'Media & Entertainment',
    'Non-Profit',
    'Government',
] as const;

// Company Sizes
export const COMPANY_SIZES = [
    { value: 'startup', label: 'Startup (1-50)', description: 'Fast-paced, innovative environment' },
    { value: 'small', label: 'Small (51-200)', description: 'Close-knit teams, direct impact' },
    { value: 'medium', label: 'Medium (201-1000)', description: 'Balanced structure and growth' },
    { value: 'large', label: 'Large (1001-10000)', description: 'Established processes, good benefits' },
    { value: 'enterprise', label: 'Enterprise (10000+)', description: 'Global reach, extensive resources' },
] as const;

// Timeframes
export const TIMEFRAMES = [
    { value: '3months', label: 'Within 3 months' },
    { value: '6months', label: 'Within 6 months' },
    { value: '1year', label: 'Within 1 year' },
    { value: 'flexible', label: 'Flexible timeline' },
] as const;

// Benefits
export const BENEFITS = [
    'Health Insurance',
    'Dental Insurance',
    'Vision Insurance',
    'Retirement Plan (401k)',
    'Flexible Working Hours',
    'Remote Work Options',
    'Professional Development Budget',
    'Gym Membership',
    'Free Meals',
    'Stock Options',
    'Paid Time Off',
    'Maternity/Paternity Leave',
    'Mental Health Support',
    'Conference Attendance',
] as const;

// Sample Job Matches
export const SAMPLE_JOB_MATCHES: JobMatch[] = [
    {
        id: '1',
        jobId: 'job-001',
        title: 'Product Designer',
        company: 'NovaTech Labs',
        location: 'UAE',
        workType: 'remote',
        employmentType: 'full-time',
        salaryRange: { min: 30000, max: 35000, currency: 'AED' },
        matchScore: 91,
        matchReasons: [
            'Strong match for UX/UI design skills',
            'Experience with design systems',
            'Portfolio shows relevant project work'
        ],
        description: 'Join our product design team to create intuitive user experiences for cutting-edge technology solutions. You\'ll work closely with product managers and engineers to deliver exceptional digital experiences.',
        requirements: [
            '3+ years UX/UI experience',
            'Figma proficiency',
            'Design systems knowledge',
            'User research experience',
            'Prototyping skills'
        ],
        benefits: ['Health insurance', 'Remote work', 'Professional development budget', 'Stock options'],
        companyLogo: 'https://via.placeholder.com/60x60/ff6b35/ffffff?text=NT',
        postedDate: '2025-10-25',
        applicationDeadline: '2025-11-15',
        isApplied: false,
        isSaved: false,
    },
    {
        id: '2',
        jobId: 'job-002',
        title: 'UX/UI Designer',
        company: 'MindBridge AI',
        location: 'UAE',
        workType: 'hybrid',
        employmentType: 'full-time',
        salaryRange: { min: 30000, max: 35000, currency: 'AED' },
        matchScore: 87,
        matchReasons: [
            'Perfect match for AI/ML product design',
            'User research experience aligns well',
            'Collaborative design process experience'
        ],
        description: 'Design cutting-edge AI interfaces that users love. Work on revolutionary AI products that are changing how people interact with technology.',
        requirements: [
            'UX research skills',
            'Prototyping experience',
            'AI/ML product knowledge',
            'Design thinking methodology',
            'Collaborative design experience'
        ],
        benefits: ['Flexible hours', 'Learning stipend', 'Stock options', 'Health insurance'],
        companyLogo: 'https://via.placeholder.com/60x60/ffd23f/ffffff?text=MB',
        postedDate: '2025-10-24',
        applicationDeadline: '2025-11-10',
        isApplied: false,
        isSaved: true,
    },
    {
        id: '3',
        jobId: 'job-003',
        title: 'UX Researcher',
        company: 'PixelWave',
        location: 'Egypt',
        workType: 'remote',
        employmentType: 'full-time',
        salaryRange: { min: 30000, max: 35000, currency: 'AED' },
        matchScore: 77,
        matchReasons: [
            'Research methodology expertise',
            'User interview experience',
            'Data-driven design approach'
        ],
        description: 'Lead user research initiatives to inform product decisions and drive user-centered design across our product portfolio.',
        requirements: [
            'Qualitative research methods',
            'Usability testing',
            'Data analysis',
            'Survey design',
            'Stakeholder communication'
        ],
        benefits: ['Remote first', 'Conference attendance', 'Mentorship programs', 'Flexible PTO'],
        companyLogo: 'https://via.placeholder.com/60x60/9b9b9b/ffffff?text=PW',
        postedDate: '2025-10-23',
        applicationDeadline: '2025-11-20',
        isApplied: false,
        isSaved: false,
    },
];

// Sample extracted resume data
export const SAMPLE_EXTRACTED_PROFILE = {
    personalInfo: {
        firstName: 'John',
        lastName: 'Designer',
        email: 'john.designer@email.com',
        phone: '+971-50-123-4567',
        location: 'Dubai, UAE',
        linkedinUrl: 'https://linkedin.com/in/johndesigner',
        portfolioUrl: 'https://johndesigner.com',
        summary: 'Creative Product Designer with 5+ years of experience in designing user-centered digital experiences. Passionate about creating intuitive interfaces and solving complex user problems through thoughtful design solutions.'
    },
    experience: [
        {
            id: '1',
            company: 'TechStart Dubai',
            position: 'Senior Product Designer',
            startDate: '2022-01',
            endDate: '',
            isCurrentRole: true,
            description: 'Leading design for B2B SaaS platform',
            responsibilities: [
                'Lead UX/UI design for enterprise dashboard',
                'Conduct user research and usability testing',
                'Collaborate with product and engineering teams',
                'Maintain and evolve design system'
            ],
            technologies: ['Figma', 'Adobe Creative Suite', 'Principle', 'InVision']
        },
        {
            id: '2',
            company: 'Digital Agency',
            position: 'UX Designer',
            startDate: '2020-03',
            endDate: '2021-12',
            isCurrentRole: false,
            description: 'Designed digital experiences for various clients',
            responsibilities: [
                'Created wireframes and prototypes',
                'Performed user interviews and research',
                'Designed responsive web applications',
                'Presented design concepts to clients'
            ],
            technologies: ['Sketch', 'Adobe XD', 'Figma', 'Marvel']
        }
    ],
    education: [
        {
            id: '1',
            institution: 'American University of Sharjah',
            degree: 'Bachelor of Design',
            field: 'Graphic Design',
            startDate: '2016-09',
            endDate: '2020-05',
            gpa: 3.7,
            description: 'Focused on digital design and user experience principles'
        }
    ],
    skills: [
        'User Experience Design',
        'User Interface Design',
        'User Research',
        'Prototyping',
        'Wireframing',
        'Design Systems',
        'Figma',
        'Adobe Creative Suite',
        'Usability Testing',
        'Information Architecture'
    ],
    certifications: [
        {
            id: '1',
            name: 'Google UX Design Certificate',
            issuer: 'Google',
            issueDate: '2023-06',
            credentialId: 'GUX-2023-12345',
            url: 'https://coursera.org/verify/GUX-2023-12345'
        },
        {
            id: '2',
            name: 'Figma Advanced Certification',
            issuer: 'Figma',
            issueDate: '2023-03',
            credentialId: 'FIG-ADV-2023',
            url: 'https://figma.com/certificate/FIG-ADV-2023'
        }
    ]
};
